import React, { useContext } from "react";
import { addProjectPhoto, uploadDispatchPhoto, getDispatchPhotoUploadUrl } from "api/Projects";
import { CheckMarkIcon, PlusIcon } from "components/svg";
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from "components/ui/Modal";
import FileInput from "components/forms/FileInput";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import { trackActivity } from "api/ActivityTracker";
import UserContext from "context/User";


export default function PhotoUpload({
  title,
  afterUpload,
  project,
  dispatchId,
  purchaseOrderId,
  buttonScheme = "secondaryOutline",
  buttonStyles,
  disabled,
}: {
  title: string;
  project?: any;
  afterUpload?: () => void;
  dispatchId?: string;
  purchaseOrderId?: string;
  buttonScheme?: string;
  buttonStyles?: any;
  disabled?: boolean;
}) {
  const toast = useToast();
  const { user } = useContext<any>(UserContext);

  const [value, setValue] = React.useState<any>(null);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  async function _onSubmit() {
    if (value && project?.id) {
      setIsSubmitting(true);
      await fetch(value.url, {
        method: "PUT",
        headers: { "Content-Type": value?.file?.type },
        body: value?.file,
      })
        .then(async (res: any) => {
          if (res.ok) {
            
            const payload = {
              url: value?.location,
              ...(purchaseOrderId ? { purchaseOrderId } : {}),
            };

            let res;

            if (dispatchId) {
              res = await uploadDispatchPhoto({
                id: dispatchId,
                data: payload,
              });
              if (res?.id && project?.id) {
                void trackActivity({
                  module: "dispatches",
                  action: "upload_dispatch_photo",
                  actionType: "create",
                  userId: user?.id || null,
                  companyId: user?.company?.id || project?.company?.id || null,
                  entityType: "dispatch",
                  entityId: dispatchId,
                  metadata: {
                    projectId: project?.id ?? null,
                    purchaseOrderId: purchaseOrderId ?? null,
                    photoId: res.id,
                    photoName: value?.file?.name ?? null,
                  },
                  userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
                });
              }
            } else {
              res = await addProjectPhoto({
                id: project.id,
                data: payload,
              });
            }
            
            if (res?.id) {
              if (project?.id) {
                void trackActivity({
                  module: "projects",
                  action: "upload_project_photo",
                  actionType: "create",
                  userId: user?.id || null,
                  companyId: user?.company?.id || null,
                  entityType: "project",
                  entityId: project.id,
                  metadata: {
                    photoId: res.id,
                  },
                  userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
                });
              }
              toast.add("Photo uploaded", "var(--theme-ui-colors-success)");
              setIsOpen(false);
              setValue(null);
              if (typeof afterUpload === "function") afterUpload();
            } else {
              toast.add(
                res?.message ? res.message : "Error uploading images",
                "var(--theme-ui-colors-danger)"
              );
            }
          }
        })
        .catch((err) => {
          toast.add("Error uploading images", "var(--theme-ui-colors-danger)");
        });
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <BasicButton
        scheme={buttonScheme}
        onClick={() => setIsOpen(!isOpen)}
        styles={buttonStyles}
        disabled={disabled}
      >
        <PlusIcon fill={buttonScheme?.toLowerCase() === "secondary" ? "#fff" : "#000"} />
        {title}
      </BasicButton>
      <Modal isOpen={isOpen} toggle={() => setIsOpen(!isOpen)} centered closeOnBackdropClick={false}>
        <ModalHeader toggle={() => setIsOpen(false)}></ModalHeader>
        <ModalBody>
          <FileInput
            directory={project?.id ? `projects/${project.id}/photos` : undefined}
            onChange={(val: any) => {
              setValue(val[0]);
            }}
            refreshValue={isSubmitting}
            multiple={false}
            presign={dispatchId ? (file: File) => getDispatchPhotoUploadUrl({ id: dispatchId, file }) : undefined}
          />
        </ModalBody>
        <ModalFooter>
          <BasicButton
            type="button"
            onClick={() => setIsOpen(false)}
            scheme="secondaryOutline"
          >
            Cancel
          </BasicButton>
          <BasicButton
            type="submit"
            onClick={_onSubmit}
            $submitting={isSubmitting}
            $iconHeight="14px"
            $iconWidth="14px"
            $loadingColor="#fff"
            keepText={true}
          >
            {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}Add Photo
          </BasicButton>
        </ModalFooter>
      </Modal>
    </>
  );
}
